import config from 'config';
import { PaginationOptions, SortOptions } from 'types/api';
import { withPagination } from 'apis/utils';
import { LabelDTO, TinyCompanyDTO } from 'types/company';
import { CommunityGroup } from './communityGroupsApi';
import { CompactLabelDTO } from 'types';
import { UserProfile } from 'types/user';
import { CommunityVisibility } from 'types/company/community';
import { Location } from 'apis/MapboxAPI';
import ApiBase from 'apis/ApiBase';
import { ApplicationCompaniesFilters } from './types';

const baseUrl = config.COMPANIES_API_URL;
const api = ApiBase();

export const applicationUrls = {
  listCompanies: (
    communityId: number,
    filters?: ApplicationCompaniesFilters,
    sortOptions?: SortOptions,
    paginationOptions?: PaginationOptions,
  ) =>
    withPagination(`${baseUrl}/communities/${communityId}/applications/companies`, paginationOptions, {
      sortBy: sortOptions?.sortBy,
      sortOrder: sortOptions?.sortOrder,
      ...filters,
    }),
  exportCompanies: (
    communityId: number,
    filters?: ApplicationCompaniesFilters,
    sortOptions?: SortOptions,
    columns?: string[],
  ) => {
    const requestParams = {
      sortBy: sortOptions?.sortBy,
      sortOrder: sortOptions?.sortOrder,
      ...filters,
      columns,
    };
    return api.get<Blob>(`${baseUrl}/communities/${communityId}/applications/companies/export`, {
      requestParams,
      responseType: 'blob',
    });
  },
  list: (communityId: number) => `${baseUrl}/communities/${communityId}/applications`,
  getForCompany: (communityId: number, companyId: number) =>
    `${baseUrl}/communities/${communityId}/applications/${companyId}`,
  listAnswersForCompany: (communityId: number, companyId: number) =>
    `${baseUrl}/communities/${communityId}/applications/${companyId}/answers`,
  answerForCompany: (communityId: number, companyId: number) =>
    `${baseUrl}/communities/${communityId}/applications/${companyId}/answers`,
  submitForCompany: (communityId: number, companyId: number) =>
    `${baseUrl}/communities/${communityId}/applications/${companyId}/submission`,
  getConfiguration: (communityId: number) => `${baseUrl}/communities/${communityId}/applications/configuration`,
  listContacts: (communityId: number, companyId: number) =>
    `${baseUrl}/communities/${communityId}/applications/${companyId}/contact`,
  updateCompanySelection: (communityId: number, companyId: number) =>
    `${baseUrl}/communities/${communityId}/applications/${companyId}/selection`,
  calculateCompleteness: (communityId: number) =>
    `${baseUrl}/communities/${communityId}/applications/calculate-completeness`,
};

export const questionTypes = ['Text', 'Boolean', 'CommunityLabel', 'CommunityGroup', 'CustomOptions'] as const;
export type ApplicationQuestionType = (typeof questionTypes)[number];

// Rating configuration types
export type RatingType = 'star' | 'number' | 'input';
export type AggregationMode = 'aggregate' | 'replace';

export interface CustomOption {
  id: UUID;
  name: string;
}

export type AnswerDTO =
  | {
      type: 'Text';
      value: string;
    }
  | {
      type: 'Boolean';
      value: boolean;
    }
  | {
      type: 'CommunityLabel';
      value: LabelDTO[];
    }
  | {
      type: 'CommunityGroup';
      value: CommunityGroup[];
    }
  | {
      type: 'CustomOptions';
      value: CustomOption[];
    };

export interface CommunityApplicationQuestion {
  id: UUID;
  question: string;
  questionDescription?: string;
  type: ApplicationQuestionType;
  groupOptions?: CommunityGroup[];
  labelOptions?: LabelDTO[];
  customOptions?: CustomOption[];
  allowMultipleSelection?: boolean;
}

export interface CommunityApplicationQuestionWithAnswer {
  question: CommunityApplicationQuestion;
  answer?: AnswerDTO;
}

export interface ProfileSection {
  name: string;
  fields: Field[];
}

export interface ApplicationConfiguration {
  profileSections: ProfileSection[];
  questions: CommunityApplicationQuestion[];
  deadlineDisplay?: DateString;
  deadlineSubmit?: DateString;
  deadlineEdit?: DateString;
  timezone?: string;
  // Rating configuration
  ratingType: RatingType;
  minRating: number;
  maxRating: number;
  aggregationMode: AggregationMode;
}

export const profileParts = [
  'Branding',
  'Employees',
  'ContactInformation',
  'EstablishedYear',
  'FundingStage',
  'ImpactGoals',
  'Industries',
  'Location',
  'MarketPotential',
  'Mission',
  'Problem',
  'ProductStage',
  'Solution',
  'TeamDescription',
  'Website',
  'WhyInvestInUs',
  'PitchDeck',
  'Videos',
] as const;

export type ProfilePart = (typeof profileParts)[number];

export type Field =
  | { type: 'profile-field'; part: ProfilePart; isRequired?: boolean }
  | { type: 'question'; questionId: UUID; isRequired?: boolean };

export interface CompanyApplication {
  questions: CommunityApplicationQuestionWithAnswer[];
  profileSections: ProfileSection[];
  submittedAt?: DateString;
  deadline: string | null;
}

export interface AnswerCommunityApplication {
  questionId: UUID;
  answer: string;
}

export interface CommunityApplication {
  companyId: number;
  answers: CommunityApplicationQuestionWithAnswer[];
}

export interface PostApplicationQuestion {
  question: string;
  questionDescription?: string | null;
  type?: ApplicationQuestionType;
  options?: UUID[];
  customOptions?: CustomOption[];
  allowMultipleSelection?: boolean;
}

export interface PatchApplicationQuestion {
  question?: string;
  questionDescription?: string;
  type?: ApplicationQuestionType;
  options?: UUID[];
  customOptions?: CustomOption[];
  allowMultipleSelection?: boolean;
}

export interface ApplicationCompanyDTO {
  profile: TinyCompanyDTO;
  labels: CompactLabelDTO[];
  fundingStage: CompactLabelDTO | null;
  stages: CompactLabelDTO[];
  groups: CompactLabelDTO[];
  industries: CompactLabelDTO[];
  companyAdmin?: UserProfile;
  joinedCommunityDate: DateString;
  submittedAt?: DateString;
  location?: Location;
  completenessPercentage?: number;
  completedParts?: CompletedPartDTO[];
  communityVisibility: CommunityVisibility;
  rating?: number;
}

export interface CompletedPartDTO {
  name: string;
  isCompleted: boolean;
}

export interface UpdateCompanySelectionDTO {
  visibility: CommunityVisibility;
}

export const updateCompanySelection = (communityId: number, companyId: number, payload: UpdateCompanySelectionDTO) => {
  return api.put(applicationUrls.updateCompanySelection(communityId, companyId), payload);
};

export interface BatchUpdateCompanySelectionDTO {
  companies: number[];
  visibility: CommunityVisibility;
}

export const batchUpdateCompanySelection = (communityId: number, payload: BatchUpdateCompanySelectionDTO) => {
  return api.put(`${baseUrl}/communities/${communityId}/applications/selections`, payload);
};

export const calculateCompleteness = (communityId: number) => {
  return api.simpleRequest('POST', applicationUrls.calculateCompleteness(communityId), {});
};
